import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { organizationSettingsKeys } from '@/lib/query-keys';
import { organizationSettingsService, type AssetKind } from '@/services/organizationSettings.service';
import type { OrganizationSettings } from '@/types/table';

export function useOrganizationSettings() {
  const queryClient = useQueryClient();

  const settingsQuery = useQuery({
    queryKey: organizationSettingsKeys.current(),
    queryFn: () => organizationSettingsService.get(),
    staleTime: 60_000,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: organizationSettingsKeys.all });

  const update = useMutation({
    mutationFn: (data: Partial<OrganizationSettings>) => organizationSettingsService.update(data),
    onSuccess: () => { invalidate(); toast.success('Organization settings saved'); },
    onError: () => toast.error('Failed to save settings'),
  });

  const uploadAsset = useMutation({
    mutationFn: ({ kind, file }: { kind: AssetKind; file: File }) => organizationSettingsService.uploadAsset(kind, file),
    onSuccess: (_, { kind }) => { invalidate(); toast.success(`${kind === 'logo' ? 'Logo' : 'Signature'} uploaded`); },
    onError: () => toast.error('Upload failed'),
  });

  const removeAsset = useMutation({
    mutationFn: (kind: AssetKind) => organizationSettingsService.removeAsset(kind),
    onSuccess: (_, kind) => { invalidate(); toast.success(`${kind === 'logo' ? 'Logo' : 'Signature'} removed`); },
    onError: () => toast.error('Remove failed'),
  });

  return {
    settings: settingsQuery.data?.data,
    isLoading: settingsQuery.isLoading,
    update,
    uploadAsset,
    removeAsset,
  };
}
